import type { InterviewCandidateProfile, TemplateSnapshot } from "@/api/generated/types";
import { Header } from "@/components/features/layout/header";
import { Checkbox } from "@/components/ui/checkbox";
import { getApiBaseUrl } from "@/lib/runtime-config";
import { markPendingInterviewAutoStart } from "@/routes/interview/auto-start";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, FileText, Loader2, ShieldCheck } from "lucide-react";
import { type ChangeEvent, type FormEvent, useMemo, useState } from "react";

interface PublicInterviewCreateResponse {
  candidateProfile: InterviewCandidateProfile;
  interviewId: string;
  maxResumeFileSize: number;
  ok: boolean;
  status: string;
  templateSnapshot: TemplateSnapshot;
}

const ALLOWED_RESUME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const RESUME_TYPE_ERROR = "職務経歴書は PDF・DOC・DOCX 形式のファイルをアップロードしてください。";
const TERMS_CONSENT_ERROR = "利用規約とプライバシーポリシーへの同意が必要です。";

function resolveApiUrl(path: string) {
  const baseUrl = getApiBaseUrl();
  return baseUrl.length > 0 ? `${baseUrl}${path}` : path;
}

function formatFileSize(bytes: number | null | undefined) {
  if (!bytes || bytes <= 0) {
    return "未登録";
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.ceil(bytes / 1024)} KB`;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return fallback;
}

export function InterviewEntryPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [reentryPrompt, setReentryPrompt] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const resumeLabel = useMemo(() => {
    if (!resumeFile) {
      return "PDF, DOC, DOCX (最大10MB)";
    }

    return `${resumeFile.name} / ${formatFileSize(resumeFile.size)}`;
  }, [resumeFile]);

  function handleResumeChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (file && !ALLOWED_RESUME_TYPES.has(file.type)) {
      setResumeFile(null);
      setPageError(RESUME_TYPE_ERROR);
      event.target.value = "";
      return;
    }
    setPageError(null);
    setResumeFile(file);
  }

  function handleTermsAcceptedChange(checked: boolean) {
    setTermsAccepted(checked);
    if (checked && pageError === TERMS_CONSENT_ERROR) {
      setPageError(null);
    }
  }

  async function submitInterview(confirmReentry: boolean) {
    if (!resumeFile) {
      return;
    }

    setIsPending(true);
    setReentryPrompt(false);

    try {
      const formData = new FormData();
      formData.set("name", name.trim());
      formData.set("email", email.trim());
      formData.set("resume", resumeFile);
      if (confirmReentry) {
        formData.set("confirmReentry", "true");
      }

      const response = await fetch(resolveApiUrl("/api/v1/public-interviews"), {
        body: formData,
        method: "POST",
      });

      if (response.status === 409) {
        const body = (await response.json().catch(() => null)) as {
          alreadyCompleted?: boolean;
          message?: string;
        } | null;
        if (body?.alreadyCompleted) {
          setReentryPrompt(true);
          return;
        }
        throw new Error(body?.message ?? "候補者情報の登録に失敗しました。");
      }

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "候補者情報の登録に失敗しました。");
      }

      const created = (await response.json()) as PublicInterviewCreateResponse;
      markPendingInterviewAutoStart(created.interviewId);
      await navigate({
        params: { interviewId: created.interviewId },
        search: (current) => current,
        to: "/u/interview/$interviewId",
      });
    } catch (error) {
      setPageError(getErrorMessage(error, "候補者情報の登録に失敗しました。"));
    } finally {
      setIsPending(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPageError(null);
    setReentryPrompt(false);

    if (name.trim().length === 0) {
      setPageError("氏名を入力してください。");
      return;
    }

    if (email.trim().length === 0) {
      setPageError("メールアドレスを入力してください。");
      return;
    }

    if (!resumeFile) {
      setPageError("職務経歴書をアップロードしてください。");
      return;
    }

    if (!termsAccepted) {
      setPageError(TERMS_CONSENT_ERROR);
      return;
    }

    void submitInterview(false);
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-white text-[#152C4D]">
      <div className="flex min-h-screen flex-col">
        <Header mode="public" />

        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-8 sm:px-6 sm:py-10">
          <div className="flex w-full flex-1 flex-col">
            <div className="mb-8 flex flex-col gap-4">
              <div>
                <h1 className="text-2xl font-bold leading-tight text-[#152C4D] sm:text-3xl">
                  候補者情報・経歴書登録
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-[#255888]">
                  面談を開始する前に、エンジニアとしてのスキルと実績を把握するための情報を入力してください。AIとの音声面談は所要時間
                  約15分です。ご提出いただいた職務経歴書は、面談での質問に活用されます。
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[#2D72A6]">次は：AIとのオンライン面談（約15分）</p>
                <p className="rounded-full border border-[#C3EBEB] bg-[#E7F7F7] px-3 py-1 text-sm font-bold text-[#005B71]">
                  1 / 2
                </p>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[#D4F1FB]">
                <div className="h-full w-1/2 rounded-full bg-primary" />
              </div>
            </div>

            <form className="space-y-7" onSubmit={(event) => void handleSubmit(event)}>
              <div className="space-y-5">
                <div className="flex flex-col gap-2">
                  <label
                    className="flex items-center gap-1 text-sm font-semibold text-[#152C4D]"
                    htmlFor="candidate-name"
                  >
                    氏名 <span className="font-bold text-primary">*</span>
                  </label>
                  <input
                    id="candidate-name"
                    name="candidate_name"
                    autoComplete="name"
                    className="h-12 rounded-lg border border-[#ACE6FA] bg-white px-4 text-base text-[#152C4D] placeholder:text-[#2D72A6]/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="山田 太郎"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    className="flex items-center gap-1 text-sm font-semibold text-[#152C4D]"
                    htmlFor="candidate-email"
                  >
                    メールアドレス <span className="font-bold text-primary">*</span>
                  </label>
                  <input
                    id="candidate-email"
                    name="candidate_email"
                    autoComplete="email"
                    className="h-12 rounded-lg border border-[#ACE6FA] bg-white px-4 text-base text-[#152C4D] placeholder:text-[#2D72A6]/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="example@hireai.com"
                    required
                    spellCheck={false}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  className="flex items-center gap-1 text-sm font-semibold text-[#152C4D]"
                  htmlFor="candidate-resume"
                >
                  職務経歴書 (PDF/Word) <span className="font-bold text-primary">*</span>
                </label>
                <label
                  className="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#C3EBEB] bg-[#E7F7F7]/70 p-10 transition-all hover:border-primary hover:bg-[#D4F1FB]/70"
                  htmlFor="candidate-resume"
                >
                  <input
                    id="candidate-resume"
                    name="candidate_resume"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="absolute inset-0 cursor-pointer opacity-0"
                    required
                    type="file"
                    onChange={handleResumeChange}
                  />
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#C3EBEB] bg-white text-primary transition-transform group-hover:scale-110">
                    <FileText className="size-6" aria-hidden="true" />
                  </div>
                  <p className="mb-1 text-sm font-medium text-[#152C4D]">
                    ファイルをドラッグ＆ドロップ、または選択
                  </p>
                  <p className="text-xs text-[#2D72A6]">{resumeLabel}</p>
                </label>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-[#C3EBEB] bg-[#E7F7F7] p-4">
                <ShieldCheck className="mt-0.5 size-5 text-primary" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-[#255888]">
                  ご入力いただいたデータは暗号化され、本面談の選考目的のみに使用されます。プライバシーポリシーに基づき、厳重に管理されます。
                </p>
              </div>

              <div
                className="flex items-start gap-3 rounded-lg border border-[#C3EBEB] bg-white p-4"
                data-testid="interview-terms-consent"
              >
                <Checkbox
                  checked={termsAccepted}
                  className="mt-0.5 size-4 rounded border-[#ACE6FA] data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                  id="interview-terms-consent-checkbox"
                  onCheckedChange={(checked) => handleTermsAcceptedChange(checked === true)}
                />
                <label
                  className="text-sm font-medium leading-6 text-[#255888]"
                  htmlFor="interview-terms-consent-checkbox"
                >
                  <a
                    className="font-bold text-primary underline-offset-2 hover:underline"
                    href="https://ai.relance.jp/terms"
                    rel="noreferrer"
                    target="_blank"
                  >
                    利用規約
                  </a>
                  と
                  <a
                    className="font-bold text-primary underline-offset-2 hover:underline"
                    href="https://3-shake.com/privacypolicy/"
                    rel="noreferrer"
                    target="_blank"
                  >
                    プライバシーポリシー
                  </a>
                  に同意します
                </label>
              </div>

              {pageError && (
                <p
                  aria-live="polite"
                  className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                >
                  {pageError}
                </p>
              )}

              {reentryPrompt && (
                <div
                  data-testid="interview-reentry-confirm"
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                >
                  <p>既にこのメールアドレスで面談を実施済みです。再度実施しますか？</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => void submitInterview(true)}
                      disabled={isPending}
                      className="inline-flex h-8 items-center gap-1.5 rounded bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-70"
                    >
                      {isPending && (
                        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                      )}
                      {isPending ? "読み込み中..." : "再度実施する"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setReentryPrompt(false)}
                      className="inline-flex h-8 items-center rounded border border-slate-300 px-3 text-xs font-semibold text-slate-700"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}

              <div className="pt-1">
                <button
                  className="inline-flex h-9 w-fit items-center justify-center gap-2 rounded bg-primary px-4 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isPending}
                  type="submit"
                >
                  <span>{isPending ? "読み込み中..." : "面談を開始する"}</span>
                  {isPending ? (
                    <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <ArrowRight className="size-5" aria-hidden="true" />
                  )}
                </button>
                <p className="mt-3 text-xs text-[#2D72A6]">
                  ※ 必須項目を入力し、利用規約とプライバシーポリシーに同意してください
                </p>
              </div>
            </form>

            <footer className="mb-8 mt-12 flex flex-col items-center gap-4 border-t border-[#D4F1FB] pt-8">
              <div className="flex flex-wrap justify-center gap-6 text-sm text-[#2D72A6]">
                <a className="transition-colors hover:text-[#005B71]" href="/terms">
                  利用規約
                </a>
                <a className="transition-colors hover:text-[#005B71]" href="/privacy">
                  プライバシーポリシー
                </a>
                <a className="transition-colors hover:text-[#005B71]" href="/legal-notice">
                  特定商取引法に基づく表記
                </a>
              </div>
              <p className="text-xs text-[#2D72A6]/70">
                © 2024 Relance AI面談. All rights reserved.
              </p>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
